import esriConfig from '@arcgis/core/config.js';
import Map from '@arcgis/core/Map.js';
import Basemap from '@arcgis/core/Basemap.js';
import MapView from '@arcgis/core/views/MapView.js';
import WMTSLayer from '@arcgis/core/layers/WMTSLayer.js'
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer.js';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import Graphic from '@arcgis/core/Graphic.js';
import Circle from '@arcgis/core/geometry/Circle.js';
import LayerList from '@arcgis/core/widgets/LayerList.js';
import ScaleBar from '@arcgis/core/widgets/ScaleBar.js';
import Search from '@arcgis/core/widgets/Search.js';
import * as intl from '@arcgis/core/intl.js';

export let mapApp = window.mapApp = window.mapApp || {};

const mapViewElement = document.querySelector('#mapView');
const mapLayerElement = document.querySelector('#mapLayer');
const mapSelectElement = document.querySelector('#mapSelect > select');
const mapGeoDataElement = document.querySelector('#mapSelect input[name*="[geodata]"]');

window.addEventListener('DOMContentLoaded', function () {
  if (!mapViewElement) {
    return;
  }

  mapApp.init();
});

// init
//
mapApp.init = function init() {
  getMapConfig();
  getLayerData();

  if (mapApp.config.apiKey === null) {
    return;
  }

  esriConfig.apiKey = mapApp.config.apiKey;
  intl.setLocale(mapApp.config.ui.locale);

  initMap();

  if (!mapApp.map) {
    return;
  }

  initMapView();
  initFeatureLayers();
  initMapLayers();

  getGeoData();
}

// configuration for map app from element data attributes
//
function getMapConfig() {
  mapApp.config = {
    apiKey: mapViewElement.dataset.esri ? mapViewElement.dataset.esri : null,
    webMapId: mapViewElement.dataset.webmap ? mapViewElement.dataset.webmap : null,

    // special WMTS base map layer
    wmtsLayer: {
      active: true,
      url: 'https://sgx.geodatenzentrum.de/wmts_basemapde/1.0.0/WMTSCapabilities.xml',
      activeLayer: {
        id: 'de_basemapde_web_raster_grau',
        tileMatrixSetId: 'Grau'
      }
    },

    // widgets in map view
    ui: {
      scalebar: mapViewElement.dataset.uiScalebar === 'true' ? true : false,
      search: mapViewElement.dataset.uiSearch === 'true' ? true : false,
      pointer: mapViewElement.dataset.uiPointer === 'true' ? true : false,
      locale: mapViewElement.dataset.uiLocale ? mapViewElement.dataset.uiLocale : 'de'
    },

    // show autobahn feature layers
    // note: every layer increases loading time
    // note: branches layer will always be shown if mapSelectElement is given
    featureLayers: {
      branches: mapViewElement.dataset.featureBranches === 'true' ? true : false,
      highwayOverview: mapViewElement.dataset.featureHighways === 'true' ? true : false,
      highwayDetails: mapViewElement.dataset.featureHighwayDetails === 'true' ? true : false,
      highwayNumbers: mapViewElement.dataset.featureHighwayNumbers === 'true' ? true : false,
    },

    view: {
      center: [11.2724081, 50.8761026],
      zoom: 5,
    }
  };

  mapApp.data = {
    view: mapApp.config.view,
    branch: ''
  }
}

// data for layers from element
//
function getLayerData() {
  if (!mapLayerElement) {
    mapApp.layers = null;
    return;
  }

  mapApp.layers = {
    geodata: mapLayerElement.dataset.geodata ? JSON.parse(mapLayerElement.dataset.geodata) : null,
    layers: mapLayerElement.dataset.layers ? JSON.parse(mapLayerElement.dataset.layers) : null,
    config: mapLayerElement.dataset.config ? JSON.parse(mapLayerElement.dataset.config) : null,
    active: []
  }

  if (mapApp.layers.geodata
    && mapApp.layers.geodata.zoom
    && mapApp.layers.geodata.center.longitude
    && mapApp.layers.geodata.center.latitude
  ) {
    mapApp.config.view = {
      center: [
        mapApp.layers.geodata.center.longitude,
        mapApp.layers.geodata.center.latitude
      ],
      zoom: mapApp.layers.geodata.zoom,
    };
    mapApp.data.view = mapApp.config.view;
  } else if (mapApp.layers.config
    && mapApp.layers.config.defZoom
    && mapApp.layers.config.defLon
    && mapApp.layers.config.defLat
  ) {
    mapApp.config.view = {
      center: [
        mapApp.layers.config.defLon,
        mapApp.layers.config.defLat
      ],
      zoom: mapApp.layers.config.defZoom,
    };
  }
}


// inits Webmap or Basemap
//
function initMap() {
  if (mapApp.map) {
    return;
  }

  // basemap layer service
  let basemap = 'arcgis-topographic';

  // use webmap portal item if set
  if (mapApp.config.webMapId) {
    basemap = new Basemap({
      portalItem: {
        id: mapApp.config.webMapId
      }
    });
  }

  // use special layer for basemap if set
  if (mapApp.config.wmtsLayer.active) {
    // underlaying for geocoding services
    const baseLayer = new VectorTileLayer({
      url: 'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer',
      visible: false
    })

    const wmtsLayer = new WMTSLayer({
      url: mapApp.config.wmtsLayer.url,
      activeLayer: {
        id: mapApp.config.wmtsLayer.activeLayer.id,
        tileMatrixSetId: mapApp.config.wmtsLayer.activeLayer.tileMatrixSetId
      },
    })

    basemap = new Basemap({
      baseLayers: [
        baseLayer,
        wmtsLayer
      ]
    });
  }

  mapApp.map = new Map({
    basemap: basemap
  });
}

// inits MapView and UI,
// incl. widgets and pointGraphic
//
function initMapView() {
  if (mapApp.view) {
    return;
  }

  // new view
  const view = mapApp.view = new MapView({
    map: mapApp.map,
    center: mapApp.config.view.center,
    zoom: mapApp.config.view.zoom,
    container: 'mapView'
  });

  mapViewElement.classList.remove('inactive');

  // add pointer marker layout
  const pointLayout = {
    type: 'simple-marker',
    color: [37, 167, 94, 1],
    outline: {
      width: 8,
      color: [37, 167, 94, 0.3]
    }
  };

  mapApp.pointGraphic = new Graphic({
    symbol: pointLayout,
  });

  view.graphics.add(mapApp.pointGraphic);

  // add scalebar widget
  if (mapApp.config.ui.scalebar === true) {
    const scalebar = new ScaleBar({
      view: view,
      unit: 'metric'
    });

    view.ui.add(scalebar, 'bottom-left');
  }

  // add search widget
  if (mapApp.config.ui.search === true) {
    const searchWidget = new Search({
      view: view,
      resultGraphicEnabled: false,
      allPlaceholder: '',
      includeDefaultSources: false,
      sources: [
        {
          name: 'ArcGIS World Geocoding Service',
          placeholder: '',
          apiKey: mapApp.config.apiKey,
          singleLineFieldName: 'SingleLine',
          url: 'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer'
        }
      ],

      goToOverride: function (view, options) {
        return view.goTo(
          options.target,
          { animation: true, duration: 1000 }
        ).then(() => {
          // set point and query branch
          mapApp.data.view.center = [view.center.longitude, view.center.latitude];
          mapApp.data.view.zoom = view.zoom;

          setPoint();
          queryBranchesLayer();
        });
      }
    });

    view.ui.add(searchWidget, 'top-right');
  }

  // click on map
  if (mapApp.config.ui.pointer === true) {
    view.on('click', function (event) {
      mapApp.data.view.center = [event.mapPoint.longitude, event.mapPoint.latitude];
      mapApp.data.view.zoom = view.zoom;

      setPoint();
      goTo();
      queryBranchesLayer();
    });
  }

  // update view
  goTo();
}

// inits feature layers on map if needed
//
function initFeatureLayers() {
  if (mapApp.config.featureLayers.branches || mapSelectElement) {
    const branchesLayer = mapApp.branchesLayer = new FeatureLayer({
      url: 'https://services-eu1.arcgis.com/46eZsDVh7oveCuwo/ArcGIS/rest/services/Niederlassungen/FeatureServer/3',
    })

    mapApp.map.add(branchesLayer);
  }

  if (mapApp.config.featureLayers.highwayOverview) {
    const highwaysOverviewLayer = mapApp.highwaysOverviewLayer = new FeatureLayer({
      url: 'https://services-eu1.arcgis.com/46eZsDVh7oveCuwo/ArcGIS/rest/services/Autobahnen/FeatureServer/9',
    })

    mapApp.map.add(highwaysOverviewLayer);
  }

  if (mapApp.config.featureLayers.highwayDetails) {
    const highwaysDetailLayer = mapApp.highwaysDetailLayer = new FeatureLayer({
      url: 'https://services-eu1.arcgis.com/46eZsDVh7oveCuwo/ArcGIS/rest/services/Autobahnen/FeatureServer/15',
    })

    mapApp.map.add(highwaysDetailLayer);
  }

  if (mapApp.config.featureLayers.highwayNumbers) {
    const highwayNumbersLayer = mapApp.highwayNumbersLayer = new FeatureLayer({
      url: 'https://services-eu1.arcgis.com/46eZsDVh7oveCuwo/ArcGIS/rest/services/Autobahnen/FeatureServer/8',
    })

    mapApp.map.add(highwayNumbersLayer);
  }
}

// query branche feature layer
// resolves geometry to branch name
//
function queryBranchesLayer() {
  if (!mapSelectElement || !mapApp.branchesLayer) {
    return;
  }

  const queryGeometry = {
    type: 'point',
    longitude: mapApp.data.view.center[0],
    latitude: mapApp.data.view.center[1]
  };

  const branchQuery = {
    spatialRelationship: 'intersects', // Relationship operation to apply
    geometry: queryGeometry,  // The feature geometry
    outFields: ['Niederlassung', 'GlobalID', 'Kuerzel', 'NL_Code'], // Attributes to return
  };

  mapApp.branchesLayer.queryFeatures(branchQuery)
    .then((results) => {
      if (results.features.length === 0) {
        unsetMapSelect();
      } else {
        // console.log(results.features[0].attributes);
        setMapSelect(results.features[0].attributes.Niederlassung);
      }
    }).catch((error) => {
      console.log(error);
    });
}

// inits layers for drawing items on map
// items fetched from #mapLayer data attributes
//
function initMapLayers() {
  if (mapApp.layers === null || mapApp.layers.layers === null) {
    return;
  }

  hideMapLayersFromLayerList();

  if ('layers' in mapApp.layers) {
    mapViewElement.classList.remove('inactive');

    drawMapItems(mapApp.layers.layers);

    // zoom more out for small view ports
    if (window.innerWidth < 992 || window.innerWidth < window.innerHeight) {
      mapApp.config.view.zoom = mapApp.config.view.zoom - 1;
      mapApp.data.view = mapApp.config.view;
    }

    // update view
    goTo();
  } else {
    removeLayers();
  }
}

// draws items on layers, based on layer data if given
// fetched from #mapLayer data attributes
//
function drawMapItems(layers) {
  removeLayers();

  // show layer list as legend widget in map
  if (layers.length > 1) {
    const layerlist = new LayerList({
      view: mapApp.view,
    });

    mapApp.view.ui.add(layerlist, 'top-right');

    if (mapLegendElement) {
      mapApp.view.ui.add(mapLegendElement, 'top-right');
      mapLegendElement.classList.remove('d-none');
    }
  }

  const popupTemplate = {
    title: '{Name}',
    content: '{Description}',
  };

  for (const layerKey in layers) {
    const layer = layers[layerKey];

    if (!layer.items || !Array.isArray(layer.items)) {
      return;
    }

    let graphicsLayer = new GraphicsLayer({
      id: layer.name,
      title: layer.title,
      graphics: [],
    });

    mapApp.map.add(graphicsLayer);
    mapApp.layers.active.push(graphicsLayer);

    layer.items.forEach((item) => {
      // popup content
      let description = '<p class="esri-feature-content__desc mb-5">' + item.description + '</p>';

      if (item.date) {
        description += '<p class="esri-feature-content__date mb-5">' + item.date + '</p>';
      }

      if (item.link) {
        let linkText = 'Mehr erfahren';

        if (item.link.includes('mailto:')) {
          linkText = 'E-Mail';
        }

        description += '<a class="esri-feature-content__link mb-5" href="' + item.link + '" target="_top">' + linkText + '</a>';
      }

      const attributes = {
        Name: item.title,
        Description: description,
        Link: item.link
      }

      // set circle (to visualize perimeter and hold popup)
      if (item.perimeter > 0) {
        const circleGeometry = new Circle({
          center: [item.longitude, item.latitude],
          geodesic: true,
          numberOfPoints: 180,
          radius: item.perimeter,
          radiusUnit: 'meters'
        });

        const circleGraphic = new Graphic({
          geometry: circleGeometry,
          // attributes: attributes,
          // popupTemplate: popupTemplate,
          symbol: {
            type: 'simple-fill',
            color: layer.perimeterColor,
            outline: {
              width: 0,
            }
          }
        });

        graphicsLayer.graphics.add(circleGraphic);
      }

      // set (centered) point
      const pointGeometry = {
        type: 'point',
        longitude: item.longitude,
        latitude: item.latitude
      };

      const pointGraphic = new Graphic({
        geometry: pointGeometry,
        attributes: attributes,
        popupTemplate: popupTemplate,
        symbol: {
          type: 'simple-marker',
          color: layer.color,
          outline: {
            color: layer.outlineColor,
            width: 4,
          },
        }
      });

      graphicsLayer.graphics.add(pointGraphic);
    })
  }

  // make layerList Legend
  if (layers.length > 1) {
    mapApp.map.load()
      .then(function () {
        return window.mapApp.map.basemap.load();
      })
      .then(function () {
        const layerListContainer = document.querySelector('.esri-layer-list');

        for (const layerKey in layers) {
          const layer = layers[layerKey];
          const layerListItem = layerListContainer.querySelector('.esri-layer-list__item-title[title="' + layer.title + '"]').parentNode.parentNode;
          const layerListItemToggle = layerListItem.querySelector('.esri-layer-list__item-toggle');
          layerListItemToggle.style.backgroundColor = 'rgba(' + layer.rgba + ')';
        };
        return;
      })
      .catch(function (error) {
        console.error(error);
      });
  }
}

// hide layers
//
function hideMapLayersFromLayerList() {
  if (typeof mapApp.map.when !== 'function') {
    return;
  }

  mapApp.map.when(function () {
    const layerTitles = mapApp.layers.active.map(layer => layer.title);

    mapApp.map.layers.items.forEach((layer) => {
      if (!layerTitles.includes(layer.title)) {
        layer.listMode = 'hide';
      }
    });
  });
}

// removes all layers
//
function removeLayers() {
  mapApp.layers.active.forEach((layer) => {
    mapApp.map.remove(layer)
  });
}

// set point on selected geometry
//
function setPoint() {
  // new point
  const pointGeometry = {
    type: 'point',
    longitude: mapApp.data.view.center[0],
    latitude: mapApp.data.view.center[1]
  };

  // update point
  mapApp.pointGraphic.geometry = pointGeometry;
}

// unset point
//
function unsetPoint() {
  // new point
  const pointGeometry = {
    type: 'point',
    longitude: 0,
    latitude: 0
  };

  // update point
  mapApp.pointGraphic.geometry = pointGeometry;
}

// set/update field mapSelectElement
//
function setMapSelect(branch) {
  if (branch) {
    mapSelectElement.value = mapApp.data.view.branch = branch;
    mapSelectElement.dispatchEvent(new Event('change'));
    setGeoData();
  } else {
    unsetMapSelect();
  }
}

// unset field value of mapSelectElement
//
function unsetMapSelect() {
  mapSelectElement.value = mapApp.data.view.branch = '';
  mapSelectElement.dispatchEvent(new Event('change'));
  unsetPoint();
  unsetGeoData();
}

// set/update field mapGeoDataElement
// saves selected geodata as json string
//
function setGeoData() {
  if (!mapGeoDataElement) {
    return;
  }

  mapGeoDataElement.value = JSON.stringify(mapApp.data.view);
}

// get saved json geodata from field mapGeoDataElement
// show point
//
function getGeoData() {
  if (!mapGeoDataElement || !mapSelectElement) {
    return;
  }

  if (!mapGeoDataElement.value || mapGeoDataElement.value === '') {
    unsetMapSelect();
    return;
  }

  try {
    mapApp.data.view = JSON.parse(mapGeoDataElement.value);
    setPoint();
    goTo();
  } catch (error) {
    return console.error(error);
  }
}

// unset field value of mapGeoDataElement
//
function unsetGeoData() {
  if (!mapGeoDataElement) {
    return;
  }

  mapGeoDataElement.value = '';
}

// view.goTo when view
//
function goTo() {
  mapApp.view.when(function () {
    mapApp.view.goTo(
      {
        center: mapApp.data.view ? mapApp.data.view.center : mapApp.config.view.center,
        zoom: mapApp.data.view ? mapApp.data.view.zoom : mapApp.config.view.zoom
      },
      {
        animation: true,
        duration: 1000
      }
    ).catch(function (error) {
      // console.error(error);
    });
  }).catch(function (error) {
    // console.error(error);
  });
}

export default {};

